import { useState, useEffect, useRef } from "react";
import { loadFromSupabase, saveToSupabase } from "../supabaseDB";
import { obfuscateKey, revealKey, fetchNewsDataDirect } from "./apiSettingsHelpers";

/* GFI Nexus — "API Keys & Data Sources" settings screen
   ─────────────────────────────────────────────────────────────────
   fix-dashboard.md "Fase B del roadmap Forecast & Market
   Intelligence". Config-only screen for the 8 external APIs the
   Forecast v2 plan (CLAUDE.md → "FOB PRICE FORECAST v2") will
   eventually consume. Each row is independently editable/testable/
   saveable — there's no single big form here, matching how
   CompanyProfile/SweetLeafSettingsPanel are their own separate
   screens rather than tabs of one shared "Settings" shell (this app
   doesn't have one).

   SECURITY: api_key is base64-"obfuscated" before it's persisted to
   Supabase — NOT real encryption, trivially reversible client-side.
   Documented risk per the spec's own explicit allowance ("no es
   blocker"). The safer pattern already used in this codebase is a
   Netlify environment variable per API (see
   netlify/functions/usda-prices-fetch.js's USDA_MARKET_NEWS_API_KEY);
   netlify/functions/api-proxy.js checks for that FIRST and only
   falls back to whatever is saved here. */

var DEFAULT_APIS = [
  {
    key: "usda_market_news",
    nameEn: "USDA Market News API", nameEs: "USDA Market News API",
    descEn: "7-year historical FOB price base for the forecast model. Basic Auth.",
    descEs: "Base histórica de 7 años de precios FOB para el modelo de proyección. Basic Auth.",
    defaultEndpoint: "https://marsapi.ams.usda.gov",
  },
  {
    key: "noaa_weather",
    nameEn: "NOAA Weather API", nameEs: "NOAA Weather API",
    descEn: "Climate anomalies in producing regions (Michoacán, Jalisco, California, Florida, Georgia). Token header.",
    descEs: "Anomalías climáticas en regiones productoras (Michoacán, Jalisco, California, Florida, Georgia). Token en header.",
    defaultEndpoint: "https://www.ncdc.noaa.gov/cdo-web/api/v2",
  },
  {
    key: "openweather",
    nameEn: "OpenWeather API", nameEs: "OpenWeather API",
    descEn: "5-day weather forecast, free tier. appid query param.",
    descEs: "Pronóstico de 5 días, tier gratuito. appid como query param.",
    defaultEndpoint: "https://api.openweathermap.org/data/2.5",
  },
  {
    /* key stays "gdelt" (not "newsdata") so an already-saved Supabase
       row for this slot keeps working — fix-dashboard.md asked to
       REPLACE GDELT Project with NewsData.io in this slot, not add a
       new one. Same slug-stability precedent as exchangerate_host
       (→Frankfurter) below. Only display name/description/default
       endpoint changed, plus NO_AUTH_APIS below (NewsData requires a
       key; GDELT didn't). */
    key: "gdelt",
    nameEn: "NewsData.io", nameEs: "NewsData.io",
    descEn: "Global news API for market intelligence. 200 requests/day free tier.",
    descEs: "API de noticias globales para inteligencia de mercado. 200 requests/día en el tier gratuito.",
    defaultEndpoint: "https://newsdata.io/api/1",
  },
  {
    key: "google_news_rss",
    nameEn: "Google News RSS", nameEs: "Google News RSS",
    descEn: "Keyword-filtered news feeds (berry prices, Mexico produce, USDA, tariff). Public, no auth.",
    descEs: "Feeds de noticias filtrados por keyword (berry prices, Mexico produce, USDA, tariff). Público, sin auth.",
    defaultEndpoint: "https://news.google.com/rss/search",
  },
  {
    key: "claude_api",
    nameEn: "Claude API", nameEs: "Claude API",
    descEn: "Daily context analysis + price-adjustment reasoning. x-api-key header.",
    descEs: "Análisis diario de contexto + razonamiento de ajuste de precio. Header x-api-key.",
    defaultEndpoint: "https://api.anthropic.com/v1/messages",
  },
  {
    /* key stays "exchangerate_host" (not "frankfurter") so an already-
       saved Supabase row / api-proxy switch case / NO_AUTH_APIS entry
       for this slot keeps working — fix-dashboard.md asked to RENAME
       this entry (exchangerate.host → Frankfurter), not add a new one.
       Only the display name/description/default endpoint changed. */
    key: "exchangerate_host",
    nameEn: "Frankfurter (Currency)", nameEs: "Frankfurter (Divisa)",
    descEn: "MXN/USD exchange rate — European Central Bank data via Frankfurter. Free, no key or registration required.",
    descEs: "Tipo de cambio MXN/USD — datos del Banco Central Europeo vía Frankfurter. Gratis, sin key ni registro.",
    defaultEndpoint: "https://api.frankfurter.app",
  },
  {
    key: "us_eia",
    nameEn: "US EIA (Energy Info Admin)", nameEs: "US EIA (Energy Info Admin)",
    descEn: "Diesel fuel prices affecting logistics cost. api_key query param.",
    descEs: "Precios de diésel que afectan el costo logístico. api_key como query param.",
    defaultEndpoint: "https://api.eia.gov/v2",
  },
];

var T = {
  en: {
    title: "API Keys & Data Sources", subtitle: "Configuration for the Forecast & Market Intelligence data sources.",
    apiKey: "API Key / Token", endpoint: "Endpoint URL", enabled: "Enabled",
    lastSync: "Last sync", never: "Never", save: "Save", saving: "Saving…",
    test: "Test Connection", testing: "Testing…",
    statusOk: "Active", statusUnconfigured: "Not configured", statusError: "Error", statusSlow: "Slow but reachable",
    show: "Show", hide: "Hide", saved: "Saved", securityNote: "API keys are base64-encoded before saving — this is obfuscation, not real encryption. For production use, set the matching Netlify environment variable instead (see code comments); it always takes priority over what's saved here.",
    noKeyRequired: "Not required for this API",
  },
  es: {
    title: "Claves API y Fuentes de Datos", subtitle: "Configuración de las fuentes de datos de Forecast & Market Intelligence.",
    apiKey: "Clave API / Token", endpoint: "URL del Endpoint", enabled: "Habilitado",
    lastSync: "Última sincronización", never: "Nunca", save: "Guardar", saving: "Guardando…",
    test: "Probar Conexión", testing: "Probando…",
    statusOk: "Activo", statusUnconfigured: "Sin configurar", statusError: "Error", statusSlow: "Lento pero alcanzable",
    show: "Mostrar", hide: "Ocultar", saved: "Guardado", securityNote: "Las claves API se guardan codificadas en base64 — esto es ofuscación, no encriptación real. Para uso en producción, configura la variable de entorno de Netlify correspondiente (ver comentarios en el código); esa siempre tiene prioridad sobre lo guardado aquí.",
    noKeyRequired: "No requerida para esta API",
  },
};


function emptyRowFor(def) {
  return {
    id: def.key,
    apiName: def.nameEn,
    apiKey: "",
    endpointUrl: def.defaultEndpoint,
    enabled: false,
    lastSync: null,
    status: "unconfigured",
  };
}

function fmtLastSync(iso, lang) {
  if (!iso) return null;
  try {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString(lang === "es" ? "es-MX" : "en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch (e) { return iso; }
}

/* APIs that need no key at all — matches apiSettingsHelpers.js's
   NO_AUTH_APIS (duplicated per CLAUDE.md's per-file small-constant
   convention rather than importing an internal implementation
   detail). The key input is disabled/hidden for these two. NewsData.io
   (slug "gdelt") requires a key, unlike the GDELT provider it
   replaced, so it is NOT in this map. */
var NO_AUTH_APIS = { google_news_rss: true, exchangerate_host: true };

var STATUS_STYLES = {
  ok: { bg: "rgba(22,163,74,0.10)", text: "#16a34a", border: "rgba(22,163,74,0.3)", icon: "✓" },
  unconfigured: { bg: "rgba(107,114,128,0.10)", text: "#6B7280", border: "rgba(107,114,128,0.3)", icon: "⏱" },
  error: { bg: "rgba(220,38,38,0.10)", text: "#DC2626", border: "rgba(220,38,38,0.3)", icon: "✗" },
  /* Distinct from "error" so a slow-but-reachable API doesn't read as
     misconfigured — originated for GDELT's proxy timeouts, kept as a
     generic status any API's test can report. */
  slow: { bg: "rgba(217,119,6,0.10)", text: "#D97706", border: "rgba(217,119,6,0.3)", icon: "⏳" },
};

var inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

function ApiRow({ row, lang, onChange, onSave, onTest, savingId, testingId }) {
  var t = T[lang === "es" ? "es" : "en"];
  var def = DEFAULT_APIS.find(function (d) { return d.key === row.id; });
  var [revealed, setRevealed] = useState(false);
  var ss = STATUS_STYLES[row.status] || STATUS_STYLES.unconfigured;
  var busy = savingId === row.id || testingId === row.id;

  return (
    <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 16, marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12, gap: 10 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{lang === "es" ? def.nameEs : def.nameEn}</div>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2, maxWidth: 520 }}>{lang === "es" ? def.descEs : def.descEn}</div>
        </div>
        <span style={{ padding: "3px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, background: ss.bg, color: ss.text, border: "1px solid " + ss.border, whiteSpace: "nowrap" }}>
          {ss.icon} {row.status === "ok" ? t.statusOk : row.status === "error" ? t.statusError : row.status === "slow" ? t.statusSlow : t.statusUnconfigured}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 2fr auto", gap: 10, alignItems: "end" }}>
        <div>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{t.apiKey}</label>
          {NO_AUTH_APIS[row.id] ? (
            <input type="text" disabled value={t.noKeyRequired} style={{ ...inp, color: "var(--color-text-muted)", fontStyle: "italic", cursor: "not-allowed" }} />
          ) : (
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type={revealed ? "text" : "password"}
                value={row.apiKey}
                onChange={function (e) { onChange(row.id, { apiKey: e.target.value }); }}
                style={inp}
                placeholder="••••••••"
              />
              <button type="button" onClick={function () { setRevealed(!revealed); }} style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 14, whiteSpace: "nowrap" }} title={revealed ? t.hide : t.show}>
                {revealed ? "🙈" : "👁"}
              </button>
            </div>
          )}
        </div>
        <div>
          <label style={{ display: "block", fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{t.endpoint}</label>
          <input
            type="text"
            value={row.endpointUrl || ""}
            onChange={function (e) { onChange(row.id, { endpointUrl: e.target.value }); }}
            style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
          />
        </div>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, cursor: "pointer", paddingBottom: 8 }}>
          <input type="checkbox" checked={!!row.enabled} onChange={function (e) { onChange(row.id, { enabled: e.target.checked }); }} />
          {t.enabled}
        </label>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
        <span style={{ fontSize: 10, color: "var(--color-text-muted)" }}>
          {t.lastSync}: {fmtLastSync(row.lastSync, lang) || t.never}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" disabled={busy} onClick={function () { onTest(row.id); }} style={{ padding: "6px 14px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 600, opacity: busy ? 0.6 : 1 }}>
            {testingId === row.id ? t.testing : t.test}
          </button>
          <button type="button" disabled={busy} onClick={function () { onSave(row.id); }} style={{ padding: "6px 14px", borderRadius: 6, border: "none", background: "var(--color-accent)", color: "#fff", cursor: busy ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: busy ? 0.6 : 1 }}>
            {savingId === row.id ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ApiSettingsRegistry({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "es" ? "es" : "en"];
  var [rows, setRows] = useState(DEFAULT_APIS.map(emptyRowFor));
  var [loading, setLoading] = useState(true);
  var [savingId, setSavingId] = useState(null);
  var [testingId, setTestingId] = useState(null);
  var [toast, setToast] = useState(null);
  var aliveRef = useRef(true);

  useEffect(function () {
    aliveRef.current = true;
    loadFromSupabase("api_settings")
      .then(function (saved) {
        if (!aliveRef.current) return;
        var byId = {};
        (saved || []).forEach(function (r) { byId[r.id] = r; });
        setRows(DEFAULT_APIS.map(function (def) {
          var existing = byId[def.key];
          if (!existing) return emptyRowFor(def);
          return {
            id: def.key,
            apiName: existing.apiName || def.nameEn,
            apiKey: revealKey(existing.apiKey || ""),
            endpointUrl: existing.endpointUrl || def.defaultEndpoint,
            enabled: !!existing.enabled,
            lastSync: existing.lastSync || null,
            status: existing.status || "unconfigured",
          };
        }));
      })
      .catch(function (e) { console.warn("[ApiSettingsRegistry] load failed:", e && e.message); })
      .finally(function () { if (aliveRef.current) setLoading(false); });
    return function () { aliveRef.current = false; };
  }, []);

  var updateRow = function (id, patch) {
    setRows(function (prev) { return prev.map(function (r) { return r.id === id ? { ...r, ...patch } : r; }); });
  };

  var handleSave = async function (id) {
    if (savingId) return;
    setSavingId(id);
    try {
      var row = rows.find(function (r) { return r.id === id; });
      var payload = { ...row, apiKey: obfuscateKey(row.apiKey) };
      var saved = await saveToSupabase("api_settings", payload);
      updateRow(id, { status: saved.status || row.status });
      setToast(t.saved);
      setTimeout(function () { setToast(null); }, 1800);
    } catch (e) {
      setToast("⚠ " + (e && e.message ? e.message : String(e)));
    } finally {
      setSavingId(null);
    }
  };

  var handleTest = async function (id) {
    if (testingId) return;
    setTestingId(id);
    try {
      var row = rows.find(function (r) { return r.id === id; });
      var data;
      if (id === "gdelt") {
        /* NewsData.io — direct-from-browser (CORS-enabled), same
           pattern GDELT used in this slot before it. Requires a key,
           so bail out early with a clear message if none is saved
           yet rather than letting the request fail generically.
           Validates status "success" in the body, not just HTTP 200
           (fix-dashboard.md point 6). */
        if (!row.apiKey) {
          data = { ok: false, error: lang === "es" ? "Falta la API Key" : "API Key is required" };
        } else {
          try {
            await fetchNewsDataDirect("/latest?apikey=" + encodeURIComponent(row.apiKey) + "&q=berry&size=1", { endpointUrl: row.endpointUrl });
            data = { ok: true };
          } catch (newsErr) {
            var newsMsg = newsErr && newsErr.message ? newsErr.message : String(newsErr);
            data = { ok: false, slow: newsMsg.indexOf("timeout") !== -1, error: newsMsg };
          }
        }
      } else {
        var res = await fetch("/.netlify/functions/api-proxy", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ apiName: id, endpointUrl: row.endpointUrl, apiKey: row.apiKey }),
        });
        data = await res.json().catch(function () { return { ok: false }; });
      }
      var nowIso = new Date().toISOString();
      var newStatus = data && data.ok ? "ok" : (data && data.slow ? "slow" : "error");
      updateRow(id, { status: newStatus, lastSync: nowIso });
      /* Surface the REAL rejection reason (invalid key body from the
         provider, CORS/network failure, timeout, etc.) instead of just
         flipping the badge to "error" silently — a generic red ✗ was
         indistinguishable between "key not saved yet", "key wrong",
         and "CORS blocked", which is exactly what made the reported
         "keys are being rejected" bug impossible to diagnose further
         without this. */
      if (data && !data.ok) {
        setToast("⚠ " + (data.message || data.error || "HTTP " + data.statusCode));
        setTimeout(function () { setToast(null); }, 5000);
      }
      /* Persist the test result too — status/lastSync should survive a
         refresh even if the user doesn't click Save afterward. */
      var freshRow = { ...row, status: newStatus, lastSync: nowIso, apiKey: obfuscateKey(row.apiKey) };
      await saveToSupabase("api_settings", freshRow).catch(function () {});
    } catch (e) {
      updateRow(id, { status: "error", lastSync: new Date().toISOString() });
      setToast("⚠ " + (e && e.message ? e.message : String(e)));
      setTimeout(function () { setToast(null); }, 5000);
    } finally {
      setTestingId(null);
    }
  };

  if (loading) return null;

  return (
    <div style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 4 }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
      <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: "0 0 14px" }}>{t.subtitle}</p>
      <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.25)", color: "#B45309", fontSize: 11.5, marginBottom: 16 }}>
        {"⚠ "}{t.securityNote}
      </div>
      {rows.map(function (row) {
        return <ApiRow key={row.id} row={row} lang={lang} onChange={updateRow} onSave={handleSave} onTest={handleTest} savingId={savingId} testingId={testingId} />;
      })}
      {toast && (
        <div style={{ position: "fixed", bottom: 20, right: 20, padding: "10px 16px", borderRadius: 8, background: "#1B3A4B", color: "#fff", fontSize: 12, fontWeight: 600, boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 3000 }}>
          {toast}
        </div>
      )}
    </div>
  );
}
